import React, { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import NextHead from 'next/head'
import FirstPage from '@/components/HomePage/FirstPage'
import Adv from '@/components/HomePage/Adv'
import WujiePromoBar from '@/components/WujiePromoBar'
import { Button } from 'antd'
import IconFont from '@/components/IconFonts'
import usePartners from '@/hooks/swr/usePartners'
import useLogin from '@/hooks/swr/useLogin'
import useNews from '@/hooks/swr/useNews'
import HomeBackground from '@/components/HomePage/HomeBackground'
import AdminNotice from '@/components/Admin/AdminNotice'
import HomeSeoSection from '@/components/Seo/HomeSeoSection'
import { serverGet } from '@/lib/serverApi'
import type { GetStaticProps } from 'next'
import type { SeoProps } from '@/utils/seo'

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '灵缇赛事系统支持哪些比赛项目？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '支持球拍类（匹克球 / 网球 / 羽毛球 / 乒乓球）的双打 / 单打、循环赛、淘汰赛、小组淘汰赛；以及棋牌类（掼蛋 / 桥牌 / 升级 / 双扣 / 斗地主）的瑞士轮 / 循环赛 / 复式赛 / 团体赛等多种赛制。',
      },
    },
    {
      '@type': 'Question',
      name: '球拍类小组淘汰赛是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '球拍类小组淘汰赛是"小组循环 + 单败淘汰"赛制：先按蛇形分组进行组内单循环，每组取前 N 名进入淘汰赛阶段，依次产生 1/8 / 1/4 决赛、半决赛、决赛和季军赛。系统自动晋级，无需人工排表。适用于匹克球、网球、羽毛球、乒乓球等。',
      },
    },
    {
      '@type': 'Question',
      name: '如何创建一场匹克球小组淘汰赛？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '访问 hy.lingti.com/match 点"创建比赛"，在 Tab 切到"🏓 球拍类赛事"，选"球拍类小组淘汰赛"。配置小组数（2/4/8）、每组晋级数（1/2/4）、目标分（11/15/21）、决胜局数（单局 / 三局两胜 / 五局三胜）、单打或双打模式。录入选手 + 创建场地后，点"一键编排"即可一次性生成全部小组对局。',
      },
    },
    {
      '@type': 'Question',
      name: '16 队怎么分组？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '常用配置：4 组 × 4 队（每组 6 场循环，共 24 场小组赛），每组前 2 进 1/4 决赛 → 半决赛 → 决赛 + 季军赛。也可以 2 组 × 8 队、8 组 × 2 队等组合，系统按 N(N-1)/2 自动算出组内总场次。',
      },
    },
    {
      '@type': 'Question',
      name: '球拍类项目支持单打吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '支持。在比赛设置中打开"单打模式"开关，每队只录入 1 人。系统会在比分卡和结算面板上自动隐藏第二位选手字段。',
      },
    },
    {
      '@type': 'Question',
      name: '计分规则可以自定义吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '可以。每局目标分（11 / 15 / 21）、领先分（1 / 2）、封顶分、决胜局数（单局 / 三局两胜 / 五局三胜）、计分制（rally 每球得分 / side_out 发球得分）均可在赛事设置中调整。',
      },
    },
    {
      '@type': 'Question',
      name: '是否自动决出冠亚季？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是。小组阶段全部结算后，系统按"胜场分 → 总得分 → 对手总分"排序每组前 N 名进淘汰赛，按 1×N、2×(N-1) 标准 mirror 配对，半决赛后自动生成决赛与季军赛，最终冠、亚、季、第四名自动判定。',
      },
    },
    {
      '@type': 'Question',
      name: '灵缇赛事系统收费吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '基础功能完全免费。线上赛、VIP 高级功能按账户灵缇点扣费。匹克球 / 网球 / 羽毛球等球类赛事的基本编排与结算不收费。',
      },
    },
    {
      '@type': 'Question',
      name: '掼蛋比赛与球类比赛使用同一系统吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是同一个平台、同一个账号。在创建比赛时切换"棋牌赛事"和"球拍类赛事"Tab。已有掼蛋赛事不受球拍类功能上线影响，互不干扰。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系赛事主理人？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '匹克球、网球、羽毛球、乒乓球等球拍类赛事咨询，以及灵缇三代复式发牌机预定，请添加赛事主理人微信 lingtij。也可通过公众号「灵缇互娱」联系。',
      },
    },
  ],
}

const Device = dynamic(() => import('@/components/HomePage/Device'), { ssr: false })
const PhotoWall = dynamic(() => import('@/components/HomePage/PhotoWall'), { ssr: false })
const Selected = dynamic(() => import('@/components/HomePage/Selected'), { ssr: false })
const Goods = dynamic(() => import('@/components/HomePage/Goods'), { ssr: false })
const Partner = dynamic(() => import('@/components/HomePage/Partner'), { ssr: false })
const News = dynamic(() => import('@/components/HomePage/News'), { ssr: false })
const Footer = dynamic(() => import('@/components/HomePage/Footer'), { ssr: false })

type SectionProps = {
  children: React.ReactNode
  paddingTop?: boolean
}

const Section = ({ children, paddingTop = true }: SectionProps) => (
  <section className={`${paddingTop ? 'pt-20 sm:pt-24 lg:pt-28' : ''} px-4 sm:px-6 lg:px-12`}>
    <div className="mx-auto w-full max-w-6xl">{children}</div>
  </section>
)

type LazySectionProps = SectionProps & {
  minHeight?: number
}

const LazySection = ({ children, minHeight = 320, paddingTop = true }: LazySectionProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    if (shouldRender) return undefined

    if (!('IntersectionObserver' in window)) {
      setShouldRender(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true)
          observer.disconnect()
        }
      },
      { rootMargin: '700px 0px' }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [shouldRender])

  return (
    <Section paddingTop={paddingTop}>
      <div ref={containerRef} style={{ minHeight: shouldRender ? undefined : minHeight }}>
        {shouldRender ? children : null}
      </div>
    </Section>
  )
}

const PartnerContent = () => {
  const { data: partners } = usePartners()
  return <Partner partners={partners || []} />
}

const NewsContent = () => {
  const { data: news } = useNews()
  return <News news={news || []} />
}

const Home = ({ latest = [] }: { latest?: React.ComponentProps<typeof HomeSeoSection>['latest'] }) => {
  const { isLogin } = useLogin()
  return (
    <div className="relative min-h-screen text-white">
      <NextHead>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD) }}
        />
      </NextHead>
      <HomeBackground />
      <main className="relative z-10 flex flex-col gap-6 pb-24 pt-12 sm:gap-10">
        {/* 平台公告置顶；用户关闭后 AdminNotice 自带的容器 + 整段会一起消失 */}
        <AdminNotice />
        <Section paddingTop={false}>
          <WujiePromoBar />
        </Section>
        <Section paddingTop={false}>
          <FirstPage isLogin={isLogin} />
        </Section>
        <Section>
          <Adv />
        </Section>
        <LazySection minHeight={620}>
          <Device />
        </LazySection>
        <LazySection minHeight={420}>
          <PhotoWall />
        </LazySection>
        <LazySection minHeight={620}>
          <Selected />
        </LazySection>
        <LazySection minHeight={420}>
          <Goods />
        </LazySection>
        <LazySection minHeight={360}>
          <PartnerContent />
        </LazySection>
        <LazySection minHeight={320}>
          <NewsContent />
        </LazySection>
        <Section>
          <HomeSeoSection latest={latest} />
        </Section>
        <LazySection minHeight={180}>
          <Footer />
        </LazySection>
      </main>
      <Button
        type="link"
        size="large"
        onClick={() => window.open('https://xiemala.com/s/wObhjT')}
        shape="round"
        className="fixed bottom-20 right-6 z-50 h-[60px] w-[60px] border-4 border-white bg-primary text-white"
      >
        <IconFont icon="icon-kefu" />
      </Button>
    </div>
  )
}

// ISR:最新资讯每 10 分钟刷新一次,首页保持静态速度
export const getStaticProps: GetStaticProps = async () => {
  const res = await serverGet<any>('users/posts', { page: 0, pageSize: 8, category: '', key: '' })
  const latest = (res?.posts ?? []).map((p) => ({
    id: p.id,
    short_id: p.short_id || null,
    title: p.title,
    date: (p.published_at || p.created_at || '').slice(0, 10) || null,
  }))
  const seo: SeoProps = {
    title: '灵缇赛事平台 · 掼蛋/桥牌/匹克球比赛编排计分系统 | 灵缇互娱',
    description:
      '灵缇赛事系统:掼蛋瑞士制/团团转/复式赛、桥牌米切尔移位、匹克球/网球/羽毛球小组循环淘汰赛一键编排;在线报名、扫码签到、扫码录分、实时排名、证书投屏。已承办 15,000+ 场比赛,基础功能免费。',
    path: '/',
  }
  return { props: { latest, seo }, revalidate: 600 }
}

export default Home
